import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticlesByCategory, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const category = categories.find((c) => c.slug === slug);

  const articles = useMemo(() => {
    if (!slug) return [];
    let list = getArticlesByCategory(slug);
    if (sortBy === 'hot') {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [slug, sortBy]);

  if (!category) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">分类不存在</h1>
        <p className="text-foreground-500 mb-6">抱歉，您访问的分类页面不存在。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  // SEO data
  const pageTitle = `${category.name} - 吃瓜爆料`;
  const pageDesc = `${category.description}。汇集最新${category.name}吃瓜内容，每日更新热点爆料。`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${category.name},吃瓜爆料,黑料,爆料,${category.name}吃瓜`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-secondary-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-900 font-medium">{category.name}</span>
          </nav>
        </div>
      </div>

      {/* Category header */}
      <section className="bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-500">
              <i className={`${category.icon} text-2xl text-background-50`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{category.name}</h1>
              <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sort & filters */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{articles.length}</strong> 条爆料
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSortBy('latest')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                sortBy === 'latest'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
              }`}
            >
              最新
            </button>
            <button
              onClick={() => setSortBy('hot')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                sortBy === 'hot'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
              }`}
            >
              最热
            </button>
          </div>
        </div>
      </div>

      {/* Article list */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
        {articles.length > 0 ? (
          <div className="flex flex-col gap-4">
            {articles.map((article, idx) => (
              <div
                key={article.id}
                className="animate-fadeInUp"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <ArticleCard article={article} variant="horizontal" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-foreground-500">该分类下暂无内容，敬请期待。</p>
          </div>
        )}
      </div>

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": pageTitle,
          "description": pageDesc,
          "url": `https://chigua.vercel.app/category/${slug}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": "吃瓜爆料",
            "url": "https://chigua.vercel.app"
          }
        })}
      </script>
    </>
  );
}